import { 
  collection, 
  getDocs,
  query, 
  where, 
  onSnapshot
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { CHAINS } from '../config/chains';
import { withRateLimitRetry, isRateLimitError } from '../utils/rateLimitRetry';

const REQUESTS_COLLECTION = 'requests';
const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;
const APPLICATION_NAME = 'Stabletrust';

// Helper function for exponential backoff retry
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

function getBackendApiBase() {
  const v = (BACKEND_API_URL || '').trim().replace(/\/+$/, '');
  if (!v) {
    throw new Error('Missing REACT_APP_BACKEND_API_URL');
  }
  return v;
}

async function apiFetch(path, options = {}) {
  const base = getBackendApiBase();
  const response = await fetch(`${base}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const msg = payload?.error || `API request failed (${response.status})`;
    const err = new Error(msg);
    err.status = response.status;
    err.payload = payload;
    throw err;
  }

  return payload;
}

/** Rich console diagnostics when POST /api/requests fails (status, body, payload snapshot). */
function logRequestCreateFailure(error, requestData, label = 'RequestService') {
  const status = error?.status;
  const payload = error?.payload;
  const summary = requestData
    ? {
        requestId: requestData.requestId,
        userAddress: requestData.userAddress,
        chainId: requestData.chainId,
        chainKey: requestData.chainKey,
        operationKind: requestData.operationKind,
        type: requestData.type,
        transactionHash: requestData.transactionHash,
        applicationName: requestData.applicationName,
        details: requestData.details,
        hasSenderCipher: Boolean(requestData.encryptedSenderRandomness),
        hasReceiverCipher: Boolean(requestData.encryptedReceiverRandomness),
        hasQueuedSenderCipher: Boolean(requestData.queuedEncryptedSenderRandomness),
        hasQueuedReceiverCipher: Boolean(requestData.queuedEncryptedReceiverRandomness),
        hasSenderCipherId: Boolean(requestData.encryptedSenderRandomnessId),
        hasReceiverCipherId: Boolean(requestData.encryptedReceiverRandomnessId),
        hasQueuedSenderCipherId: Boolean(requestData.queuedEncryptedSenderRandomnessId),
        hasQueuedReceiverCipherId: Boolean(requestData.queuedEncryptedReceiverRandomnessId),
      }
    : null;
  console.error(`[${label}] POST /api/requests failed`, {
    message: error?.message,
    status,
    serverError: payload?.error,
    serverIssues: payload?.issues,
    fullPayload: payload,
    requestSummary: summary,
    stack: error?.stack,
  });
}

const isRetryableError = (error) => {
  // Check for rate limit errors first
  if (isRateLimitError(error)) {
    return true;
  }
  
  // Retry on network errors and temporary Firebase errors
  const code = error?.code;
  const message = error?.message?.toLowerCase() || '';
  
  // Firebase error codes that are retryable (excluding rate limits which are handled above)
  const retryableCodes = [
    'unavailable',
    'deadline-exceeded',
    'aborted',
    'internal',
    'unauthenticated' // Sometimes auth errors are temporary
  ];
  
  // Network-related errors
  if (code && retryableCodes.includes(code.toLowerCase())) {
    return true;
  }
  
  // Check for network-related error messages
  if (message.includes('network') || 
      message.includes('timeout') || 
      message.includes('fetch') ||
      message.includes('connection')) {
    return true;
  }
  
  return false;
};

/** Map a Firestore doc to a request row; returns null if chainId filter excludes the doc. */
function buildRequestItem(doc, chainId) {
  const data = doc.data();
  if (!chainId) {
    return { id: doc.id, ...data };
  }

  let docChainId = data.chainId;
  let filterChainId = chainId;

  if (typeof docChainId === 'string' && CHAINS[docChainId]) {
    const chainConfig = CHAINS[docChainId];
    docChainId =
      chainConfig.chainIdDecimal ||
      (typeof chainConfig.chainId === 'string' && chainConfig.chainId.startsWith('0x')
        ? parseInt(chainConfig.chainId, 16)
        : Number(chainConfig.chainId));
  } else if (typeof docChainId === 'string' && docChainId.startsWith('0x')) {
    docChainId = parseInt(docChainId, 16);
  } else {
    docChainId = Number(docChainId);
  }

  if (typeof filterChainId === 'string' && CHAINS[filterChainId]) {
    const chainConfig = CHAINS[filterChainId];
    filterChainId =
      chainConfig.chainIdDecimal ||
      (typeof chainConfig.chainId === 'string' && chainConfig.chainId.startsWith('0x')
        ? parseInt(chainConfig.chainId, 16)
        : Number(chainConfig.chainId));
  } else if (typeof filterChainId === 'string' && filterChainId.startsWith('0x')) {
    filterChainId = parseInt(filterChainId, 16);
  } else {
    filterChainId = Number(filterChainId);
  }

  let matchesById = false;
  if (doc.id.includes(':')) {
    const docIdParts = doc.id.split(':');
    const docIdChainKey = docIdParts[0];
    if (CHAINS[docIdChainKey]) {
      const chainConfig = CHAINS[docIdChainKey];
      const docIdChainId =
        chainConfig.chainIdDecimal ||
        (typeof chainConfig.chainId === 'string' && chainConfig.chainId.startsWith('0x')
          ? parseInt(chainConfig.chainId, 16)
          : Number(chainConfig.chainId));
      matchesById = docIdChainId === filterChainId;
    }
  }

  const chainIdsMatch = !isNaN(docChainId) && !isNaN(filterChainId) && docChainId === filterChainId;
  if (chainIdsMatch || matchesById) {
    return { id: doc.id, ...data };
  }
  return null;
}

function requestCreatedAtMs(row) {
  const ca = row?.createdAt;
  if (ca && typeof ca.toMillis === 'function') return ca.toMillis();
  if (ca instanceof Date) return ca.getTime();
  return 0;
}

/**
 * Firestore auto-indexes single-field equality on userAddress. We avoid orderBy here so
 * no composite index is required (indexes may be missing or still building in prod).
 * Filter chain, then sort by createdAt in memory.
 */
function filterSortAndLimitRequestSnapshot(snapshot, chainId, maxRequests) {
  const rows = [];
  snapshot.forEach((d) => {
    const row = buildRequestItem(d, chainId);
    if (row) rows.push(row);
  });
  rows.sort((a, b) => requestCreatedAtMs(b) - requestCreatedAtMs(a));
  if (rows.length > maxRequests) {
    console.warn(
      `Warning: User ${userAddress} has ${rows.length}+ matching requests; showing newest ${maxRequests}.`,
    );
    return rows.slice(0, maxRequests);
  }
  return rows;
}

export class RequestService {
  
  /**
   * Add a new pending request to Firebase with retry logic
   * Retries up to 3 times with exponential backoff for retryable errors
   */
  static async addRequest(requestData, retryOptions = { maxRetries: 3, baseDelayMs: 1000 }) {
    const { maxRetries = 3, baseDelayMs = 1000 } = retryOptions;

    // Always send applicationName so the backend can attribute requests to this app.
    const payload = {
      ...requestData,
      applicationName: requestData.applicationName || APPLICATION_NAME,
    };
    
    const { requestId } = payload;
    let lastError = null;

    // Use withRateLimitRetry for rate limit errors, but also handle other retryable errors
    return await withRateLimitRetry(
      async () => {
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
          try {
            if (attempt > 0) {
              // Exponential backoff: 1s, 2s, 4s
              const delayMs = baseDelayMs * Math.pow(2, attempt - 1);
              await sleep(delayMs);
            }

            const result = await apiFetch('/api/requests', {
              method: 'POST',
              body: JSON.stringify(payload),
            });
            const id = result?.id || requestId;
            return id;
          } catch (error) {
            lastError = error;
            console.error(`Error adding request (attempt ${attempt + 1}/${maxRetries + 1}):`, error);
            logRequestCreateFailure(error, payload, 'RequestService.addRequest');

            // Rate limit errors will be handled by withRateLimitRetry wrapper
            if (isRateLimitError(error)) {
              throw error; // Let withRateLimitRetry handle it
            }
            
            // Check if this is a retryable error and we haven't exhausted retries
            if (attempt < maxRetries && isRetryableError(error)) {
              continue;
            }
            
            // If not retryable or we've exhausted retries, throw
            if (!isRetryableError(error) || attempt === maxRetries) {
              throw error;
            }
          }
        }
        
        // Should never reach here, but just in case
        throw lastError || new Error('Failed to add request after all retries');
      },
      {
        retries: 5, // Additional retries for rate limits on top of the internal retries
        baseDelayMs: 1000,
        maxDelayMs: 30000
      }
    );
    
  }

  /**
   * Update request status
   * @param {string} requestId - The request ID (can be in format "chainId:userAddress:requestId", "chainId:requestId", or just "requestId")
   * @param {string} status - The new status
   * @param {object} additionalData - Additional data to update
   * @param {string|number} chainId - Optional chainId to construct document ID if requestId doesn't include it
   * @param {string} userAddress - Optional userAddress to construct document ID if requestId doesn't include it
   */
  static async updateRequestStatus(requestId, status, additionalData = {}, chainId = null, userAddress = null) {
    return await withRateLimitRetry(
      async () => {
        try {
          // If requestId already includes separators, use it directly
          // Otherwise, construct the document ID using provided chainId and userAddress
          // For backward compatibility, also try older formats if update fails
          let documentId = requestId;
          
          // Check if requestId already has the full format (chainId:userAddress:requestId or chainId:requestId)
          if (!requestId.includes(':')) {
            // No separators, construct from provided parameters
            if (chainId && userAddress) {
              // New format: chainId:userAddress:requestId
              documentId = `${String(chainId)}:${String(userAddress).toLowerCase()}:${requestId}`;
            } else if (chainId) {
              // Fallback format: chainId:requestId
              documentId = `${String(chainId)}:${requestId}`;
            }
            // If neither provided, use requestId as-is (backward compatibility)
          }
          
        } catch (error) {
          console.error('Error updating request status:', error);
          throw error;
        }
      },
      {
        retries: 5,
        baseDelayMs: 1000,
        maxDelayMs: 30000
      }
    );
  }

  /**
   * Get all requests for a user (optionally filtered by chainId)
   * Note: Firestore has a 1MB limit per query. We set a high limit (10000) to ensure
   * all requests are returned. If a user has more than 10000 requests, pagination
   * would need to be implemented.
   */
  static async getUserRequests(userAddress, chainId = null) {
    return await withRateLimitRetry(
      async () => {
        try {
          // Set a high limit to ensure all requests are fetched (Firestore max is ~1MB per query)
          // 10000 should cover most users while staying well under the 1MB limit
          const MAX_REQUESTS = 10000;
          const addr = String(userAddress || '').toLowerCase();
          if (!addr) return [];

          const qByUserAddress = query(
            collection(db, REQUESTS_COLLECTION),
            where('schemaVersion', '==', 2),
            where('userAddress', '==', addr),
          );

          const snap = await getDocs(qByUserAddress);
          const requests = filterSortAndLimitRequestSnapshot(
            snap,
            chainId,
            MAX_REQUESTS,
          );

          return requests;
        } catch (error) {
          console.error('Error getting user requests:', error);
          throw error;
        }
      },
      {
        retries: 5,
        baseDelayMs: 1000,
        maxDelayMs: 30000
      }
    );
  }

  /**
   * Listen to real-time updates for user requests (optionally filtered by chainId)
   * Note: Firestore has a 1MB limit per query. We set a high limit (10000) to ensure
   * all requests are returned. If a user has more than 10000 requests, pagination
   * would need to be implemented.
   */
  static subscribeToUserRequests(userAddress, callback, chainId = null) {
    if (!userAddress) return () => {};

    // Set a high limit to ensure all requests are fetched (Firestore max is ~1MB per query)
    // 10000 should cover most users while staying well under the 1MB limit
    const MAX_REQUESTS = 10000;
    const addr = String(userAddress || '').toLowerCase();
    if (!addr) return () => {};

    const qByUserAddress = query(
      collection(db, REQUESTS_COLLECTION),
      where('schemaVersion', '==', 2),
      where('userAddress', '==', addr),
    );

    const unsubOwned = onSnapshot(
      qByUserAddress,
      (s) => {
        const requests = filterSortAndLimitRequestSnapshot(
          s,
          chainId,
          MAX_REQUESTS,
        );
        callback(requests);
      },
      (error) => {
        console.error('Error listening to user requests:', error);
        // Let callers exit loading states gracefully on listener failures.
        callback([]);
      }
    );
    return () => unsubOwned();
  }

  /**
   * Delete a completed request
   * DISABLED: Deletion is not allowed. This function is kept for backward compatibility
   * but will throw an error if called.
   */
  static async deleteRequest(requestId) {
    console.error('deleteRequest called but deletion is disabled. Request ID:', requestId);
    throw new Error('Request deletion is not allowed');
  }

  /**
   * Clean up old completed requests
   * DISABLED: Deletion is not allowed. This function is kept for backward compatibility
   * but will throw an error if called.
   */
  static async cleanupOldRequests(userAddress, olderThanDays = 30) {
    console.error('cleanupOldRequests called but deletion is disabled. User:', userAddress);
    throw new Error('Request deletion is not allowed');
  }
}
